import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { operations as lmsOperations } from 'store/lmsManager'
import { isResourceComplete } from 'utils/resourceCompletionHelpers'
import { useCourseManager } from 'layers/content/v2/hooks/useCourseManager'
import {
  isTier2PreviewMode,
  isDigitalWellBeingPreviewMode,
} from 'utils/routing/routeHelpers'
import {
  handleTieredInterventionToggleComplete,
  getProgramForCourseManager,
} from './helpers'
import {
  SimpleMarkCompleteButton,
  MarkCompleteIconWrapper,
  ThemedMarkCompleteButtonIcon,
} from './component.styles'

const TieredInterventionMarkCompleteComponent = ({
  courseNodeId,
  instructorData,
  program,
  userContext,
  onMarkComplete,
  fetchInstructorData,
  fetchInstructorProgramFlexGet,
  allowAnyRegistrationMatch,
  strictCompletionUpdate,
  useProgramFlexRefresh,
  useSecondaryMarkCompleteButton = false,
}) => {
  // Local state to manage completion status
  const [isComplete, setIsComplete] = useState(false)

  // Get courseManager using the useCourseManager hook
  const courseManager = useCourseManager({
    program: getProgramForCourseManager(program),
  })

  // Get the registration ID from URL query params
  const registrationId =
    new URLSearchParams(window.location.search).get('class') || ''

  // Guard against transient module/HMR export mismatches in local dev
  const isTier2Preview =
    typeof isTier2PreviewMode === 'function' ? isTier2PreviewMode() : false
  const isDigitalWellBeingPreview =
    typeof isDigitalWellBeingPreviewMode === 'function'
      ? isDigitalWellBeingPreviewMode()
      : false

  // Check if URL contains "preview" to hide the button
  if (isTier2Preview || isDigitalWellBeingPreview) {
    return null
  }

  // Update completion status when instructorData changes
  useEffect(() => {
    if (instructorData && courseNodeId) {
      const completionStatus = isResourceComplete({
        instructorData,
        courseNodeId,
        program,
        registrationId,
        allowAnyRegistrationMatch,
      })
      setIsComplete(completionStatus)
    }
  }, [
    instructorData,
    courseNodeId,
    program,
    registrationId,
    allowAnyRegistrationMatch,
  ])

  const handleToggleComplete = async () => {
    if (isComplete || !courseNodeId || !instructorData) {
      return
    }

    try {
      const didMarkComplete = await handleTieredInterventionToggleComplete({
        courseNodeId,
        instructorData,
        program,
        userContext,
        courseManager,
        registrationId,
        onMarkComplete,
      })

      if (strictCompletionUpdate && !didMarkComplete) {
        return
      }

      // Update local state immediately after successful completion
      setIsComplete(true)

      // Refresh instructor data to ensure the UI reflects the latest state
      if (useProgramFlexRefresh && fetchInstructorProgramFlexGet) {
        await fetchInstructorProgramFlexGet()
      } else if (fetchInstructorData) {
        await fetchInstructorData()
      }
    } catch (error) {
      console.error('Failed to mark complete:', error)
      // Don't update state if the operation failed
    }
  }

  return (
    <SimpleMarkCompleteButton
      disabled={isComplete}
      isComplete={isComplete}
      onClick={e => {
        handleToggleComplete()
      }}
      useSecondaryMarkCompleteButton={useSecondaryMarkCompleteButton}
    >
      {isComplete && (
        <MarkCompleteIconWrapper>
          <ThemedMarkCompleteButtonIcon
            useSecondaryMarkCompleteButton={useSecondaryMarkCompleteButton}
          />
        </MarkCompleteIconWrapper>
      )}
      {isComplete ? 'Done' : 'Mark Done'}
    </SimpleMarkCompleteButton>
  )
}

TieredInterventionMarkCompleteComponent.propTypes = {
  allowAnyRegistrationMatch: PropTypes.bool,
  courseNodeId: PropTypes.string,
  fetchInstructorData: PropTypes.func,
  fetchInstructorProgramFlexGet: PropTypes.func,
  instructorData: PropTypes.object,
  isComplete: PropTypes.bool,
  match: PropTypes.func,
  onMarkComplete: PropTypes.func,
  program: PropTypes.string,
  strictCompletionUpdate: PropTypes.bool,
  useProgramFlexRefresh: PropTypes.bool,
  useSecondaryMarkCompleteButton: PropTypes.bool,
  userContext: PropTypes.object,
}

const TieredInterventionMarkComplete = connect(null, {
  onMarkComplete: lmsOperations.makeAttemptV2,
  fetchInstructorData: lmsOperations.fetchInstructorData,
  fetchInstructorProgramFlexGet: lmsOperations.fetchInstructorProgramFlexGet,
})(TieredInterventionMarkCompleteComponent)

export default TieredInterventionMarkComplete
